import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/authContext";
import PublicHeader from "../components/PublicHeader";
import PublicFooter from "../components/PublicFooter";
import apiService from "../actions/apiService";
import HomeIcon from "../assets/icon/home.svg";
import carIcon from "../assets/icon/car.svg";
import bagIcon from "../assets/icon/bag.svg";
import watchIcon from "../assets/icon/watch.svg";
// import { formatStatusLabel } from "../utils/statusUtils";

// const STATUS_COLORS = {
//   active: "bg-[#CBECF9] text-[#036]",
//   available: "bg-[#CBECF9] text-[#036]",
// };

// Map API category keys to their icon assets
const CATEGORY_ICON_BY_KEY = {
  car: carIcon,
  bag: bagIcon,
  watch: watchIcon,
  real_estate: HomeIcon,
  other: null,
};

// Category chips for filtering
const CATEGORY_CHIPS = [
  { key: "all", label: "All categories", icon: "" },
  { key: "cars", label: "Cars", icon: carIcon },
  { key: "bags", label: "Bags", icon: bagIcon },
  { key: "watches", label: "Watches", icon: watchIcon },
  { key: "real_estate", label: "Real Estate", icon: HomeIcon },
];

const SORT_OPTIONS = [
  { key: "recent", label: "Most recent" },
  { key: "price_low_high", label: "Price: Low to High" },
  { key: "price_high_low", label: "Price: High to Low" },
];

// Map backend category to frontend category
const mapCategory = (category) => {
  const categoryMap = {
    car: "car",
    bag: "bag",
    watch: "watch",
    real_estate: "real_estate",
    other: "other",
  };
  return categoryMap[category] || category || "other";
};

const HomePage = () => {
  const navigate = useNavigate();
  const { isAuthenticated, userRole, isLoading } = useAuth();
  const [items, setItems] = useState([]);
  const [itemsLoading, setItemsLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [searchQuery, setSearchQuery] = useState(""); // Separate state for actual search query
  const [page, setPage] = useState(1);
  const [category, setCategory] = useState("all"); // Category filter state
  const [sort, setSort] = useState("recent"); // Sort filter state
  const [categoryCounts, setCategoryCounts] = useState({
    all: 0,
    cars: 0,
    bags: 0,
    watches: 0,
    real_estate: 0,
  });

  // Redirect authenticated users to their dashboard
  useEffect(() => {
    if (!isLoading && isAuthenticated && userRole) {
      switch (userRole) {
        case "buyer":
          navigate("/buyer");
          break;
        case "seller":
          navigate("/seller");
          break;
        case "admin":
          navigate("/admin");
          break;
        case "super_admin":
          navigate("/super-admin");
          break;
        default:
          break;
      }
    }
  }, [isAuthenticated, userRole, isLoading, navigate]);

  // Fetch category counts on initial load (only once)
  useEffect(() => {
    let mounted = true;
    const fetchCategoryCounts = async () => {
      if (isAuthenticated || isLoading) return;

      try {
        // Fetch all items to calculate category counts
        const response = await apiService.get("/items/public/available", {
          page: 1,
          limit: 1000, // Get a large number to count all categories
        });

        if (mounted && response.success && response.data?.items) {
          const counts = {
            all: response.data.items.length || 0,
            cars: 0,
            bags: 0,
            watches: 0,
            real_estate: 0,
          };

          response.data.items.forEach((item) => {
            const cat = item.category;
            if (cat === "car") counts.cars++;
            else if (cat === "bag") counts.bags++;
            else if (cat === "watch") counts.watches++;
            else if (cat === "real_estate") counts.real_estate++;
          });

          setCategoryCounts(counts);
        }
      } catch (error) {
        console.error("Error fetching category counts:", error);
      }
    };

    if (!isLoading && !isAuthenticated) {
      fetchCategoryCounts();
    }

    return () => {
      mounted = false;
    };
  }, [isLoading, isAuthenticated]);

  // Fetch available items
  useEffect(() => {
    const fetchItems = async () => {
      if (isAuthenticated) return; // Don't fetch if authenticated

      setItemsLoading(true);
      try {
        const params = {
          page,
          limit: 100,
        };
        if (searchQuery && searchQuery.trim().length > 0) {
          params.search = searchQuery.trim();
        }

        // Map category chip to backend "type"
        const typeMap = {
          cars: "car",
          bags: "bag",
          watches: "watch",
          real_estate: "real_estate",
        };
        if (category !== "all" && category in typeMap) {
          params.type = typeMap[category];
        }

        // Add sort and order parameters
        if (sort === "recent") {
          params.sort = "created_at";
          params.order = "desc";
        } else if (sort === "price_low_high") {
          params.sort = "price";
          params.order = "asc";
        } else if (sort === "price_high_low") {
          params.sort = "price";
          params.order = "desc";
        }

        const response = await apiService.get(
          "/items/public/available",
          params
        );

        if (response.success && response.data) {
          const fetchedItems = response.data.items || [];
          setItems(fetchedItems);

          // Update category counts when fetching all items
          if (category === "all" && fetchedItems.length > 0) {
            const counts = {
              all: fetchedItems.length,
              cars: 0,
              bags: 0,
              watches: 0,
              real_estate: 0,
            };

            fetchedItems.forEach((item) => {
              const cat = item.category;
              if (cat === "car") counts.cars++;
              else if (cat === "bag") counts.bags++;
              else if (cat === "watch") counts.watches++;
              else if (cat === "real_estate") counts.real_estate++;
            });

            setCategoryCounts((prev) => ({
              ...prev,
              ...counts,
            }));
          } else if (category !== "all") {
            // Update count for the selected category
            setCategoryCounts((prev) => ({
              ...prev,
              [category]: fetchedItems.length,
            }));
          }
        }
      } catch (error) {
        console.error("Error fetching items:", error);
        setItems([]);
      } finally {
        setItemsLoading(false);
      }
    };

    if (!isLoading && !isAuthenticated) {
      fetchItems();
    }
  }, [isLoading, isAuthenticated, page, searchQuery, category, sort]);

  // Handle search button click
  const handleSearch = () => {
    setSearchQuery(search);
    setPage(1); // Reset to first page on search
  };

  // Handle Enter key press in search input
  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  // Show loading while checking auth status
  if (isLoading) {
    return (
      <div className="min-h-screen bg-esbgColor flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-esblue"></div>
      </div>
    );
  }

  // Don't render if already authenticated (will redirect)
  if (isAuthenticated) {
    return null;
  }

  return (
    <div className="min-h-screen bg-esbgColor flex flex-col">
      <PublicHeader />
      <main className="">
        <div className="flex-1 max-w-[1512px] py-4 sm:py-6 md:py-8 px-4 sm:px-6 md:px-8 lg:px-16 2xl:px-8 mx-auto w-full">
          {/* Search */}
          <div className="bg-[#2A8BF7] rounded-[12px] py-6 sm:py-8 md:py-12 lg:py-16 px-4 sm:px-5 md:px-6 lg:px-8 mb-6 sm:mb-8">
            <div className="text-center">
              <div className="text-white mb-3 sm:mb-4 font-rubik text-[22px] sm:text-[26px] md:text-[30px] lg:text-[32px] font-[600] leading-tight px-2">
                Discovery luxury items for sale
              </div>
              <div className="opacity-50 mb-6 sm:mb-8 text-white text-[13px] sm:text-[15px] md:text-[16px] lg:text-[18px] font-[400] px-2">
                Find exclusive watches, bags, cars, and properties from verified
                sellers
              </div>
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 max-w-[850px] mx-auto w-full">
                <div className="relative flex-1 w-full">
                  <input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    onKeyPress={handleKeyPress}
                    className="w-full h-10 sm:h-11 rounded-[8px] border border-gray-200 pl-9 sm:pl-10 pr-3 sm:pr-[12px] placeholder:text-[14px] sm:placeholder:text-[16px] placeholder:opacity-75 bg-white text-[14px] sm:text-[16px]"
                    placeholder="Search for an item..."
                  />
                  <div className="absolute opacity-50 left-2.5 sm:left-3 top-2.5 sm:top-3">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="18"
                      height="18"
                      viewBox="0 0 20 20"
                      fill="none"
                      className="sm:w-5 sm:h-5"
                    >
                      <path
                        d="M19 19L14.65 14.65M17 9C17 13.4183 13.4183 17 9 17C4.58172 17 1 13.4183 1 9C1 4.58172 4.58172 1 9 1C13.4183 1 17 4.58172 17 9Z"
                        stroke="#1E1E1E"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                </div>
                <button
                  onClick={handleSearch}
                  className="w-full sm:w-auto px-6 sm:px-8 py-2.5 sm:py-2 bg-white text-[#2A8BF7] rounded-[8px] font-semibold text-sm sm:text-base transition-colors whitespace-nowrap"
                >
                  Search
                </button>
              </div>
            </div>
          </div>

          {/* Items Grid */}
          {itemsLoading ? (
            <div className="bg-white border border-[#DCEDFF] rounded-[12px] p-4 sm:p-5 flex items-center justify-center py-10 sm:py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-esblue"></div>
            </div>
          ) : (
            <>
              <div className="bg-white border border-[#DCEDFF] rounded-[12px] p-4 sm:p-5 md:p-6">
                {/* Category Filters and Sort */}
                <div className="mb-6 sm:mb-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4">
                  <div className="flex items-center gap-2 sm:gap-3 md:gap-4 overflow-x-auto flex-1 w-full sm:w-auto pb-2 sm:pb-0 -mx-4 sm:mx-0 px-4 sm:px-0">
                    {CATEGORY_CHIPS.map((chip) => (
                      <button
                        key={chip.key}
                        onClick={() => {
                          setCategory(chip.key);
                          setPage(1); // Reset to first page on filter change
                        }}
                        className={`px-2.5 sm:px-3 w-fit min-w-fit max-w-fit py-2 sm:py-[8.5px] rounded-[8px] text-[12px] sm:text-[14px] whitespace-nowrap transition flex items-center gap-2 sm:gap-3 md:gap-4 border border-esBorderLightBlur ${
                          category === chip.key
                            ? "bg-esBorderLightBlur text-[#2A8BF7]"
                            : "bg-white text-gray-700"
                        }`}
                      >
                        {chip.icon && (
                          <img
                            src={chip.icon}
                            alt={chip.label}
                            className="h-5 w-5 sm:h-6 sm:w-6"
                          />
                        )}
                        <span className="hidden xs:inline">{chip.label}</span>
                        <span className="xs:hidden">
                          {chip.key === "all"
                            ? "All"
                            : chip.label.split(" ")[0]}
                        </span>
                        <span
                          className={`ml-0.5 sm:ml-1 rounded-[50px] px-2 sm:px-[10px] py-0.5 sm:py-1 text-[11px] sm:text-[14px] ${
                            category === chip.key
                              ? "bg-white text-[#575757]"
                              : "bg-[#E0E0E0] text-[#575757]"
                          }`}
                        >
                          {categoryCounts[chip.key] || 0}
                        </span>
                      </button>
                    ))}
                  </div>

                  {/* Sort Dropdown */}
                  <div className="relative inline-flex items-center gap-2 w-full sm:w-auto px-3 sm:px-[16px] py-2.5 sm:py-[13.5px] rounded-[8px] border border-gray-200 bg-white text-caption text-esdarkGray">
                    <span className="opacity-50" aria-hidden>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="18"
                        height="16"
                        viewBox="0 0 21 18"
                        fill="none"
                        className="sm:w-5 sm:h-4"
                      >
                        <path
                          d="M15.5 1V17M15.5 17L11.5 13M15.5 17L19.5 13M5.5 17V1M5.5 1L1.5 5M5.5 1L9.5 5"
                          stroke="#1E1E1E"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </span>
                    <select
                      value={sort}
                      onChange={(e) => {
                        setSort(e.target.value);
                        setPage(1); // Reset to first page on sort change
                      }}
                      className="bg-transparent w-full focus:outline-none text-[12px] sm:text-[14px] text-[#1E1E1E] cursor-pointer appearance-none pr-6 sm:pr-8"
                    >
                      {SORT_OPTIONS.map((o) => (
                        <option key={o.key} value={o.key}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                    {/* Custom Dropdown Chevron */}
                    <span className="absolute right-2 sm:right-3 pointer-events-none opacity-50">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="12"
                        height="7"
                        viewBox="0 0 14 8"
                        fill="none"
                        className="sm:w-3.5 sm:h-2"
                      >
                        <path
                          d="M1 1L7 7L13 1"
                          stroke="#1E1E1E"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </span>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5 md:gap-6">
                  {items.map((item) => {
                    const categoryKey = mapCategory(item.category);
                    const categoryIcon = CATEGORY_ICON_BY_KEY[categoryKey];

                    return (
                      <div
                        key={item.id}
                        className="bg-white w-full border border-[#DCEDFF] rounded-[20px] sm:rounded-[24px] overflow-hidden p-3 sm:p-4"
                      >
                        <div className="relative h-40 sm:h-44 md:h-48 bg-gray-100 rounded-[10px] sm:rounded-[12px] overflow-hidden">
                          {item.image ? (
                            <img
                              src={item.image}
                              alt={item.title}
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                e.currentTarget.style.display = "none";
                              }}
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-esdarkGray text-xs sm:text-sm opacity-50">
                              No image
                            </div>
                          )}
                          {categoryIcon && item.category !== "other" && (
                            <button className="absolute right-2 sm:right-3 top-2 sm:top-3 text-white bg-[#1E1E1E40] rounded-[12px] sm:rounded-[15px] h-[28px] w-[28px] sm:h-[30px] sm:w-[30px] flex items-center justify-center">
                              <img
                                src={categoryIcon}
                                alt={item.category}
                                className="w-3.5 h-3.5 sm:w-4 sm:h-4 invert"
                              />
                            </button>
                          )}
                        </div>

                        <div className="px-0 pt-4 sm:pt-5 pb-6 sm:pb-8">
                          <h3 className="font-medium leading-5 line-clamp-2 text-gray-900 break-words text-sm sm:text-base">
                            {item.title}
                          </h3>
                          <div className="mt-4 sm:mt-6 md:mt-8 grid grid-cols-2 gap-3 sm:gap-4">
                            <div>
                              <div className="text-esDarkBlue text-[11px] sm:text-caption opacity-50">
                                Sale price
                              </div>
                              <div className="text-esblue font-semibold text-sm sm:text-small-price-d mt-0.5 sm:mt-1">
                                {item.price.toFixed(2)} €
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="px-0 pb-0">
                          <Link
                            to={`/listing/${item.id}`}
                            className="block w-full bg-blue-50 rounded-[8px] text-center text-[12px] sm:text-caption-d text-esblue py-2.5 sm:py-3 transition-colors hover:bg-blue-100"
                          >
                            <span className="font-semibold">View listing</span>
                          </Link>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {items.length === 0 ? (
                  <div className="bg-white rounded-[12px] p-4 sm:p-5 text-center py-10 sm:py-12 text-esdarkGray">
                    <p className="text-sm sm:text-base">
                      No available items found.
                    </p>
                  </div>
                ) : null}
              </div>
            </>
          )}
        </div>
      </main>
      <PublicFooter />
    </div>
  );
};

export default HomePage;
